import * as i0 from '@angular/core';
import { EventEmitter, LOCALE_ID, Injectable, Inject, Directive, Output, NgModule } from '@angular/core';
import { Subscription, throwError, of } from 'rxjs';
import * as moment_ from 'moment';
import * as semver from 'semver';
import { enc, MD5, AES, mode, pad, SHA256 } from 'crypto-js';
import { SelectionModel } from '@angular/cdk/collections';
import * as i1 from '@angular/router';
import * as i1$1 from '@angular/common/http';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { tap, switchMap, catchError, map } from 'rxjs/operators';
import * as QRCode from 'qrcode';

const moment$2 = moment_;
class UtilsService {
    constructor(localeId, environment, constants) {
        this.localeId = localeId;
        this.environment = environment;
        this.constants = constants;
        this.DEFAULT_DATE_FORMAT = 'MMM Do YYYY, h:mm:ssa';
        this.ENC_KEY = '2sAZEURKPurzV1qd212h4ysxrqf16iLa4lKz7HLm';
        this.regex = {
            // eslint-disable-next-line max-len
            email: 
            // eslint-disable-next-line max-len
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            // eslint-disable-next-line max-len
            URL: /^(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/i,
            APIKey: /^Bearer\s[A-Za-z0-9-_.=]+$|^[A-Za-z0-9-_.=]+$/,
            APIKeyMDCore: /^([a-fA-F0-9])*[a-fA-F0-9]+$/,
            name: /^(?!\s)[a-zA-Z0-9\s]*$/,
            pin: /^[0-9]*$/,
            hostName: /^([a-z0-9]+(-[a-z0-9]+)*\.)*[a-z0-9-]+\.[a-z]+$/i,
            // eslint-disable-next-line max-len
            IP4: /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        };
        this.key = enc.Hex.parse(MD5(this.ENC_KEY).toString());
        this.iv = enc.Utf8.parse('opswatmetaaccess');
        this.limit = {
            name: 50,
            url: 2000,
            api: 1000,
            email: 256,
            msg: 512,
            entity: 1024,
        };
        this.fileSettings = {
            size: 10 * 1024 * 1024,
            sizeText: '10MB',
        };
        this.veriyPIN = '';
        this.apiLink = this.getApiUrl();
        this.accountNotFound = new EventEmitter();
        this.apiPinPost = [
            '/protected-apps',
            '/protected-apps/assign',
            '/secure-access/activities/sessions/terminate/user',
            '/secure-access/activities/sessions/terminate/session-uuid',
            '/api/sdp/pool',
            '/access-method/idps',
            '/wlist_cve',
            '/api/devices/action',
            '/api/groups',
            '/api/cves/update-whitelist',
            '/api/policies',
            '/user-management/users/invite-user',
            '/rebranding',
            '/group-notification',
            '/settings/integrations/updateIntegrationSetting',
            '/settings/global/updateGlobalSetting',
            '/settings/reports/updateReportAndNotificationSetting',
            '/api/policies/clone',
            '/api/devices/check-compliance',
            '/api/devices/scan-devices',
            '/api/devices/remove-app',
            '/user/acceptInvitation',
            '/user/declineInvitation',
            '/sso/updateSDPSetting',
            '/sso/updateConsoleSetting',
            '/settings/global/device/regcode/revoke',
        ];
        this.apiPinGet = [
            '/export/vulnerabilities/?requestParam=',
            '/api/devices/export/devices/?requestParam=',
            '/api/groups/export/devices/?requestParam=',
            '/api/cves/export?requestParam=',
            '/api/cves/export/app/devices?requestParam=',
            '/history/export?requestParam=',
            '/export/policy/',
            '/api/devices/export/cve-device-details?requestParam=',
            '/secure-access/activities/export/logs?requestParam=',
            '/secure-access/activities/export/sessions?requestParam=',
            '/export/monitored-accounts/?requestParam=',
            '/settings/reports/export/missing-device-report',
            '/export/report/os-patches',
            '/export/report/missing-os-patch',
            '/wlist_cve/export/devices/?requestParam=',
            '/history/export/gateway?requestParam=',
            '/export/cve-app-details?requestParam=',
        ];
        this.apiPinPut = [
            '/protected-apps/',
            '/configuration',
            '/rules',
            '/api/sdp/gateway/enable?enable=',
            '/api/sdp/pool/',
            '/api/sdp/gateway/deactivate/?ids=',
            '/api/sdp/gateway/activate/?ids=',
            '/access-method/idps/',
            '/configuration/gateway-type',
            '/api/sdp/gateway/v2/accept/',
            '/api/sdp/gateway/',
            '/api/groups/',
            '/api/idp-group',
            '/api/policies/',
            '/group-notification',
            '/rebranding',
            '/user-management/users/update',
        ];
        this.apiPinDelete = [
            '/protected-apps?ids=',
            '/api/sdp/pool/',
            '/access-method/idps?ids=',
            '/wlist_cve',
            '/wlist_cve/unbind_device?device_id=',
            '/api/cves/remove-whitelist',
            '/api/sdp/gateway?ids=',
            '/api/groups/',
            '/api/idp-group',
            '/api/policies/',
            '/group-notification',
            '/rebranding',
            '/user-management/users/delete',
        ];
        /**
         * Factory function that takes a list of file extensions to check for and returns a validator that does just that.
         *
         * @param fileExtensionsList: ['zip','jpeg',...]
         */
        this.fileExtensionValidator = (fileExtensionsList) => (control) => {
            const file = control.value;
            if (!file) {
                return null;
            }
            const fileName = file.name.toLowerCase();
            if (!fileName) {
                return { invalid: true };
            }
            for (const extension of fileExtensionsList) {
                if (fileName.endsWith(extension.toLowerCase())) {
                    return null;
                }
            }
            return { invalid: true, invalidFileExtension: true };
        };
        /**
         * Factory function returns validator object  that checks for file's size.
         *
         * @param maxFileSizeInMBs : numeric value that denotes max file size in Mega Bytes.
         */
        this.fileSizeValidator = (maxFileSizeInMBs) => (control) => {
            const file = control.value;
            if (!file) {
                return null;
            }
            const fileSizeInMBs = file.size / 1024 / 1024;
            if (fileSizeInMBs <= maxFileSizeInMBs) {
                return null;
            }
            return { invalid: true, invalidFileSize: true };
        };
        /**
         * Form Validator that checks if value is url format.
         */
        this.urlValidator = (control) => {
            const val = control.value;
            return val.startsWith('http://') || val.startsWith('https://') ? null : { invalid: true };
        };
    }
    /*
     * Checks valid email address
     */
    isEmailAddress(email) {
        return this.regex.email.test(String(email).toLowerCase());
    }
    /*
     * Checks valid url address
     */
    isURL(url) {
        return this.regex.URL.test(String(url).toLowerCase());
    }
    /*
     * Checks valid host name
     */
    isHostName(url) {
        return this.regex.hostName.test(url);
    }
    /*
     * Checks valid api key
     */
    isAPIKey(api) {
        return this.regex.APIKey.test(api);
    }
    /*
     * Check white space in string
     */
    isWhiteSpace(string) {
        return string.indexOf(' ') >= 0;
    }
    /*
     * Sets favicon
     */
    setFavicon(favicon) {
        try {
            const icons = [
                {
                    rel: 'apple-touch-icon-precomposed',
                    sizes: '144x144',
                    filename: 'apple-touch-icon-144x144.png',
                },
                {
                    rel: 'apple-touch-icon-precomposed',
                    sizes: '60x60',
                    filename: 'apple-touch-icon-60x60.png',
                },
                {
                    rel: 'apple-touch-icon-precomposed',
                    sizes: '120x120',
                    filename: 'apple-touch-icon-120x120.png',
                },
                {
                    rel: 'apple-touch-icon-precomposed',
                    sizes: '76x76',
                    filename: 'apple-touch-icon-76x76.png',
                },
                {
                    rel: 'apple-touch-icon-precomposed',
                    sizes: '152x152',
                    filename: 'apple-touch-icon-152x152.png',
                },
                {
                    rel: 'icon',
                    sizes: '96x96',
                    filename: 'favicon-96x96.png',
                },
                {
                    rel: 'icon',
                    sizes: '32x32',
                    filename: 'favicon-32x32.png',
                },
                {
                    rel: 'icon',
                    sizes: '16x16',
                    filename: 'favicon-16x16.png',
                },
            ];
            for (const icon of icons) {
                const faviconTag = document.querySelector(`link[rel="${icon.rel}"][sizes="${icon.sizes}"]`);
                faviconTag.href = `assets/images/favicon/${favicon}/${icon.filename}`;
            }
        }
        catch (err) {
            console.error(err);
        }
    }
    getConstants() {
        return this.constants;
    }
    getFeatureFlags() {
        return this.getConstants().FEATURE_FLAGS;
    }
    getEnvironment() {
        return this.environment;
    }
    getVersion() {
        return this.getEnvironment().version;
    }
    getApiUrl() {
        return this.getEnvironment().apiUrl;
    }
    getSdpDownloadUrl() {
        return this.getEnvironment().sdpDownloadUrl;
    }
    getPinManagementUrl() {
        return this.getEnvironment().pinManagementUrl;
    }
    /*
     * Returns localeId of the language that is currently displayed. This
     * corresponds to the localeId displayed in the URL. This value can be
     * different from preferred localeId. If the user navigates from /page
     * (English) to /fr/page (French) by manually changing the URL, preferred
     * localeId is still English (because it wasn't changed via the language
     * switch in the UI), but currently displayed localeId is French.
     */
    getCurrentLocaleId() {
        return this.localeId;
    }
    /*
     * Sets localeId preferred by the user
     */
    setPreferredLocaleId(localeId) {
        localStorage.setItem('localeId', localeId);
    }
    /*
     * Returns localeId preferred by the user
     */
    getPreferredLocaleId() {
        return localStorage.getItem('localeId');
    }
    /*
     * Returns the whole locale object based on localeId
     */
    getLocale(localeId) {
        // eslint-disable-next-line
        for (let i = 0; i < this.getEnvironment().languages.length; i++) {
            if (this.getEnvironment().languages[i].localeId === localeId) {
                return this.getEnvironment().languages[i];
            }
        }
    }
    /*
     * Returns the whole locale object of current locale
     */
    getCurrentLocale() {
        return this.getLocale(this.getCurrentLocaleId());
    }
    /*
     * set/get local storage object at a given key
     */
    setLsObject(key, obj) {
        localStorage.setItem(key, JSON.stringify(obj));
    }
    decrypt(message) {
        if (message === 'null' || message.trim().length < 1) {
            return '';
        }
        return AES.decrypt(message.toString(), this.key, {
            iv: this.iv,
            mode: mode.CFB,
            padding: pad.NoPadding,
        }).toString(enc.Utf8);
    }
    encrypt(message) {
        if (message === 'null' || message.trim().length < 1) {
            return '';
        }
        return AES.encrypt(message, this.key, {
            iv: this.iv,
            mode: mode.CFB,
            padding: pad.NoPadding,
        }).toString();
    }
    encryptSHA256(message) {
        if (message.length > 0) {
            return SHA256(message).toString();
        }
        return '';
    }
    getLsUser() {
        let user = localStorage.getItem('user');
        if (!user) {
            return null;
        }
        /** If the local storage value is encrypted */
        if (!user.includes('accountId')) {
            user = this.decrypt(user);
        }
        return JSON.parse(user);
    }
    getLsGuest() {
        let guest = localStorage.getItem('guest');
        if (!guest) {
            return null;
        }
        /** If the local storage value is encrypted */
        if (!guest.includes('accountId')) {
            guest = this.decrypt(guest);
        }
        return JSON.parse(guest);
    }
    getRole() {
        const lsUser = this.getLsUser();
        if (!(lsUser && typeof lsUser === 'object')) {
            return null;
        }
        return lsUser['role'];
    }
    isFreeAccount() {
        const lsUser = this.getLsUser();
        if (!(lsUser && typeof lsUser === 'object')) {
            return null;
        }
        return lsUser['free'];
    }
    /**
     * Example:
     * Input: https://gears.opswat.com/dashboard#add-devices
     * Output: https://gears.opswat.com/dashboard
     */
    getUrlNoFragments(url) {
        return url.split('#')[0];
    }
    setTheme(theme) {
        if (typeof theme === 'string') {
            const htmlElement = document.querySelector('html');
            const htmlClasses = htmlElement.classList;
            const themeClassesToRemove = Array.from(htmlClasses).filter((item) => item.includes('theme-'));
            if (themeClassesToRemove.length) {
                htmlClasses.remove(...themeClassesToRemove);
            }
            // If the theme is not default, attempt to load external CSS theme file
            if (theme !== 'default') {
                return new Promise((resolve) => {
                    this.loadTheme(theme)
                        .then(() => {
                        this.saveTheme(htmlClasses, theme);
                    })
                        .finally(() => {
                        resolve();
                    });
                });
            }
            // If the theme is default, no need to load any external CSS theme file
            this.saveTheme(htmlClasses, theme);
        }
        // If the theme is not of type string, we resolve a promise and move on with default theme
        return Promise.resolve();
    }
    loadTheme(theme) {
        if (this.getEnvironment().production) {
            // TODO: this might be getting cached, resolve
            return this.addStylesheetNode(`/themes/${theme}.theme.css`);
        }
        else {
            return this.addJavascriptNode(`/themes/${theme}.theme.js`);
        }
    }
    saveTheme(htmlClasses, theme) {
        htmlClasses.add(`theme-${theme}`);
        localStorage.setItem('theme', theme);
    }
    /*
     * Adds <link rel="stylesheet" href=href> to DOM if it doesn't exist
     */
    addStylesheetNode(href) {
        return new Promise((resolve, reject) => {
            const links = document.getElementsByTagName('link');
            // eslint-disable-next-line
            for (let i = 0; i < links.length; i++) {
                if (links[i].getAttribute('href') === href) {
                    return resolve();
                }
            }
            const stylesheetNode = document.createElement('link');
            stylesheetNode.rel = 'stylesheet';
            stylesheetNode.href = href;
            document.getElementsByTagName('head')[0].appendChild(stylesheetNode);
            stylesheetNode.onload = () => resolve();
            stylesheetNode.onerror = () => reject();
        });
    }
    /*
     * Adds <script rel="text/javascript" src=src> to DOM if it doesn't exist
     */
    addJavascriptNode(src) {
        return new Promise((resolve, reject) => {
            const scripts = document.getElementsByTagName('script');
            // eslint-disable-next-line
            for (let i = 0; i < scripts.length; i++) {
                if (scripts[i].getAttribute('src') === src) {
                    return resolve();
                }
            }
            const scriptNode = document.createElement('script');
            scriptNode.type = 'text/javascript';
            scriptNode.src = src;
            document.getElementsByTagName('head')[0].appendChild(scriptNode);
            scriptNode.onload = () => resolve();
            scriptNode.onerror = () => reject();
        });
    }
    getPreferredTheme() {
        return localStorage.getItem('theme');
    }
    /**
     * Returns cookies parsed into an object
     */
    getParsedCookies() {
        const cookies = {};
        window.document.cookie
            .split(';')
            .map((cookie) => cookie.trim().split('='))
            .forEach((cookie) => {
            if (Array.isArray(cookie) && cookie.length === 2) {
                cookies[cookie[0]] = cookie[1];
            }
        });
        return cookies;
    }
    getCookie(cookieName) {
        return this.getParsedCookies()[cookieName];
    }
    setCookie(name, path, day) {
        const d = new Date();
        d.setTime(d.getTime() + day * 24 * 60 * 60 * 1000);
        const expires = 'expires=' + d.toUTCString();
        document.cookie = name + '=' + path + ';' + expires + ';path=/';
    }
    isObjectEmpty(obj) {
        if (!obj) {
            return false;
        }
        if (typeof obj !== 'object') {
            return false;
        }
        return Object.entries(obj).length === 0 && obj.constructor === Object;
    }
    isObject(val) {
        if (val === null) {
            return false;
        }
        return typeof val === 'function' || typeof val === 'object';
    }
    getDefaultHighlightedIndex(array) {
        for (let i = 0; i < array.length; i++) {
            if (array[i].type === 'error' && array[i].value > 0) {
                return i;
            }
        }
        for (let i = 0; i < array.length; i++) {
            if (array[i].type === 'success' && array[i].value > 0) {
                return i;
            }
        }
        return 0;
    }
    severityToClass(severity) {
        return {
            low: '',
            moderate: '',
            important: '',
            critical: 'warn',
        }[severity];
    }
    stringify(array) {
        return array.join(' ');
    }
    redirectToUrl(url) {
        return (window.location.href = url);
    }
    redirectToLoginUrl() {
        const ssoLoginUrl = this.getEnvironment().ssoLoginUrl;
        return (window.location.href = ssoLoginUrl);
    }
    redirectToLogoutUrl(args) {
        const ssoLogoutUrl = this.getEnvironment().ssoLogoutUrl;
        const ref = args?.ref ? `?ref=${encodeURIComponent(args.ref)}` : '';
        return (window.location.href = `${ssoLogoutUrl}${ref}`);
    }
    /**
     * @returns Array of module routes user can be
     * redirected to upon login
     * Ex.: ['/policies', '/settings', ...]
     */
    getAllowedLoginRedirectRoutes() {
        const excludedRoutes = ['tcs', 'login', 'logout'];
        const routes = this.getEnvironment().routes;
        const isString = (key) => typeof key === 'string';
        const routes1 = Object.values(routes).filter(isString);
        const routes2 = Object.values(routes.deviceManagement).filter(isString);
        const allowedRoutes = [...routes1, ...routes2];
        return allowedRoutes.filter((key) => !excludedRoutes.includes(key)).map((key) => this.startStrWithChar(key, '/'));
    }
    startStrWithChar(str, char) {
        if (typeof str !== 'string') {
            return char;
        }
        if (typeof char !== 'string' || str.startsWith(char)) {
            return str;
        }
        return `${char}${str}`;
    }
    getLicenseKey() {
        const activeUser = this.getLsUser();
        return activeUser && activeUser['licenseKey'] ? activeUser['licenseKey'] : undefined;
    }
    mapErrorMessageToStatus(message) {
        const map = {
            notlogin: 401,
            'Invalid JWT token': 401,
        };
        if (map.hasOwnProperty(message)) {
            return map[message];
        }
        return 400;
    }
    normalizeRoleName(role) {
        role = role.toLowerCase();
        const map = {
            owner: ['owner'],
            administrator: ['administrator', 'admin'],
            readOnly: ['read-only', 'read'],
        };
        for (const key in map) {
            if (map[key].includes(role)) {
                return key;
            }
        }
    }
    normalizeOsName(os) {
        os = os.toLowerCase();
        const map = {
            Windows: ['windows', 'win'],
            macOS: ['mac', 'macos'],
            Linux: ['linux'],
            iOS: ['ios'],
            Android: ['android'],
        };
        for (const key in map) {
            if (map[key].includes(os)) {
                return key;
            }
        }
    }
    getValidRoles() {
        const roles = [];
        for (const role of Object.keys(this.getEnvironment().roles)) {
            roles.push(role);
        }
        return roles;
    }
    isValidRole(role) {
        return this.getValidRoles().includes(role);
    }
    getValidLocaleIds() {
        return this.getEnvironment().languages.map((language) => language.localeId);
    }
    isValidLocaleId(localeId) {
        return this.getValidLocaleIds().includes(localeId);
    }
    findUrlParamOfLocaleId(localeId) {
        return this.getEnvironment().languages.find((language) => language.localeId === localeId).urlParam;
    }
    unsubscribe(subscriptions) {
        for (const subscription of Object.keys(subscriptions)) {
            if (subscriptions[subscription] instanceof Subscription) {
                subscriptions[subscription].unsubscribe();
            }
        }
    }
    localInfoDataToLocaleStorageUser(briefAccount, accountInfo) {
        const user = {
            accountId: briefAccount.host.account_id,
            licenseKey: briefAccount.host.license_key,
            name: {
                whole: briefAccount.host.name,
                first: briefAccount.host.first_name,
                last: briefAccount.host.last_name,
            },
            email: briefAccount.host.email,
            role: accountInfo['read-only'] ? 'readOnly' : 'administrator',
            free: accountInfo.is_free_account,
            allowedTokens: accountInfo.allowed_token,
            usedTokens: accountInfo.used_token,
            hidden: accountInfo.hidden,
            enableSha2: accountInfo.enableSha2,
            privacy: {
                hostname: accountInfo.privacy.hostname,
            },
            isMonitoring: briefAccount.is_monitoring,
            permissiongPage: accountInfo.permission_page,
        };
        return user;
    }
    isMyAccount(accountId) {
        const currentUser = this.getLsUser();
        if (currentUser) {
            return this.getLsUser()['accountId'] === accountId;
        }
        return false;
    }
    checkPinsMatch(group) {
        const pin = group.controls.pin.value;
        const confirmPin = group.controls.confirmPin.value;
        return pin === confirmPin ? null : { notSame: true };
    }
    getRandomString(len) {
        return Math.random()
            .toString(36)
            .substring(2, 2 + len);
    }
    encryptPinEncode(pin) {
        const encodedPin = encodeURIComponent(pin).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16)));
        return btoa(encodedPin);
    }
    encryptPin(pin) {
        if (pin.length > 0) {
            let encryptedPin = '';
            for (let i = pin.length - 1; i >= 0; i--) {
                const charCode = pin.charCodeAt(i) - (i + 1);
                encryptedPin += this.getRandomString(3) + String.fromCharCode(charCode);
            }
            return this.encryptPinEncode(encryptedPin);
        }
        return '';
    }
    getPlatformOs() {
        const { userAgent } = window.navigator;
        const map = {
            'like Mac': 'ios',
            Mac: 'macos',
            Win: 'windows',
            Android: 'android',
            Linux: 'linux',
        };
        for (const key in map) {
            if (userAgent.includes(key)) {
                return map[key];
            }
        }
        return 'windows';
    }
    getDiffObject(obj1, obj2) {
        // Make sure an object to compare is provided
        if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
            return obj1;
        }
        // Variables
        const diffs = {};
        let key;
        const arraysMatch = (arr1, arr2) => {
            // Check if the arrays are the same length
            if (arr1.length !== arr2.length) {
                return false;
            }
            // Check if all items exist and are in the same order
            for (let i = 0; i < arr1.length; i++) {
                if (arr1[i] !== arr2[i]) {
                    return false;
                }
            }
            // Otherwise, return true
            return true;
        };
        // eslint-disable-next-line
        const compare = (item1, item2, key) => {
            // Get the object type
            const type1 = Object.prototype.toString.call(item1);
            const type2 = Object.prototype.toString.call(item2);
            // If type2 is undefined it has been removed
            if (type2 === '[object Undefined]') {
                diffs[key] = null;
                return;
            }
            // If items are different types
            if (type1 !== type2) {
                diffs[key] = item2;
                return;
            }
            // If an object, compare recursively
            if (type1 === '[object Object]') {
                const objDiff = this.getDiffObject(item1, item2);
                if (Object.keys(objDiff).length > 1) {
                    diffs[key] = objDiff;
                }
                return;
            }
            // If an array, compare
            if (type1 === '[object Array]') {
                if (!arraysMatch(item1, item2)) {
                    diffs[key] = item2;
                }
                return;
            }
            // Else if it's a function, convert to a string and compare
            // Otherwise, just compare
            if (type1 === '[object Function]') {
                if (item1.toString() !== item2.toString()) {
                    diffs[key] = item2;
                }
            }
            else {
                if (item1 !== item2) {
                    diffs[key] = item2;
                }
            }
        };
        // Compare our objects
        // Loop through the first object
        for (key in obj1) {
            if (obj1.hasOwnProperty(key)) {
                compare(obj1[key], obj2[key], key);
            }
        }
        // Loop through the second object and find missing items
        for (key in obj2) {
            if (obj2.hasOwnProperty(key)) {
                if (!obj1[key] && obj1[key] !== obj2[key]) {
                    diffs[key] = obj2[key];
                }
            }
        }
        // Return the object of differences
        return diffs;
    }
    compareObject(objA, objB) {
        if (objA === objB) {
            return true;
        }
        // if both objA and objB are null or undefined and exactly the same
        if (!(objA instanceof Object) || !(objB instanceof Object)) {
            return false;
        }
        // if they are not strictly equal, they both need to be Objects
        if (objA.constructor !== objB.constructor) {
            return false;
        }
        for (const p in objA) {
            if (!objA.hasOwnProperty(p)) {
                continue;
            }
            // other properties were tested using objA.constructor === objB.constructor
            if (!objB.hasOwnProperty(p)) {
                return false;
            }
            // allows to compare objA[ p ] and objB[ p ] when set to undefined
            if (objA[p] === objB[p]) {
                continue;
            }
            // if they have the same strict value or identity then they are equal
            if (typeof objA[p] !== 'object') {
                return false;
            }
            // Numbers, Strings, Functions, Booleans must be strictly equal
            if (!this.compareObject(objA[p], objB[p])) {
                return false;
            }
            // Objects and Arrays must be tested recursively
        }
        for (const p in objB) {
            if (objB.hasOwnProperty(p) && !objA.hasOwnProperty(p)) {
                return false;
            }
            // allows objA[ p ] to be set to undefined
        }
        return true;
    }
    /**
     * Example:
     * arr = [1, 2, 3, 4, 5]
     * mask = [true, false, true, false, false]
     * returns: [1, 3]
     */
    unmask(arr, mask) {
        return arr.filter((item, i) => !!mask[i]);
    }
    /**
     *  Return cloned object/array
     *
     *  @param Object cloneObj
     */
    cloneDeep(cloneObj) {
        return JSON.parse(JSON.stringify(cloneObj));
    }
    /**
     *  Return is right certification file
     *
     *  @param File file
     */
    isCertificationFile(file) {
        return (file &&
            typeof file.name === 'string' &&
            (file.name.endsWith('.pem') || file.name.endsWith('.cert') || file.name.endsWith('.crt') || file.name.endsWith('.cer')));
    }
    /**
     *  Return is right XML file
     *
     *  @param File file
     */
    isXmlFile(file) {
        return file && typeof file.name === 'string' && file.name.endsWith('.xml');
    }
    /**
     *  Return URL Pattern
     */
    getUrlPattern() {
        return /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    }
    /**
     *  Return URL Pattern
     */
    getIdpPattern() {
        return /^[a-zA-Z0-9 ]+$/;
    }
    /**
     * Return Ordinal
     */
    getOrdinal(n) {
        const arr = ['th', 'st', 'nd', 'rd'];
        const mod = n % 100;
        return n + (arr[(mod - 20) % 10] || arr[mod] || arr[0]);
    }
    toggleFieldNames(where, value, fieldNames) {
        fieldNames.forEach((fieldName) => {
            const field = where.getFormControl(fieldName);
            if (value) {
                field.enable();
            }
            else {
                field.disable();
            }
        });
    }
    isNumeric(nr) {
        return !isNaN(nr);
    }
    getId(length) {
        return Math.random()
            .toString(36)
            .substring(4, 4 + length);
    }
    getEpoch() {
        return Math.round(new Date().getTime());
    }
    /**
     * Given a url and the fields to parse from the path, function returns an object containing a mapping from fields to url paths.
     *
     * @param url Url to parse
     * @param fields Array containing the fields to retrieve from the Url.
     * fields must be in-order so that function knows which key to set during parsing.
     * ie. Url: https://metaaccess-testing.opswat.com/remediation/a/b/c
     *     fields: ['accountId', 'deviceId', 'token']
     *     returns: { 'accountId': 'a', 'deviceId': 'b', token: 'c' }
     */
    parseUrlPathForFields(url, fields) {
        // parses and removes protocol and url, leaving only the path, ie. "/remediation/a/b/c", for more sound algorithm
        const urlPath = url.replace(/^(http[s]?:\/\/)?[a-zA-Z.-]+/, '');
        // ["", "remediation", "a", "b", "c"]
        const chunks = urlPath.split('/');
        const fieldsMap = {};
        for (let i = 0; i < fields.length; i++) {
            fieldsMap[fields[fields.length - (i + 1)]] = chunks[chunks.length - (i + 1)];
        }
        return fieldsMap;
    }
    getUrlBeforeHash(rawUrl) {
        const urlQuery = rawUrl.split('#')[0];
        return urlQuery.split('?')[0];
    }
    getAbsoluteRoute(path, segments) {
        const routes = this.getEnvironment().routes;
        const value = this.getDescendant(routes, path);
        return `/${value}${segments ? '/' + segments.join('/') : ''}`;
    }
    /**
     * Returns nested object value using a dot notation string
     *
     * @param path 'name.first'
     * returns: 'First'
     */
    getDescendant(obj, path) {
        const segments = path.split('.');
        while (segments.length && (obj = obj[segments.shift()])) { }
        return obj;
    }
    /**
     * Moves item in an array from oldIndex to newIndex
     *
     * @param array array to manipulate
     * @param i oldIndex
     * @param j newIndex
     */
    moveArrayItem(array, i, j) {
        if (j >= array.length) {
            let k = j - array.length + 1;
            while (k--) {
                array.push(undefined);
            }
        }
        array.splice(j, 0, array.splice(i, 1)[0]);
        return array;
    }
    downloadFile(response, customFilename) {
        const responseBody = response.body;
        const binaryData = [];
        binaryData.push(responseBody);
        const blob = new Blob(binaryData, {
            type: responseBody.type,
        });
        const contentDispositionHeader = response.headers.get('content-disposition');
        const filenameBeMatches = typeof contentDispositionHeader === 'string' ? /^.*filename=(.*?)$/.exec(contentDispositionHeader) : [];
        const filenameBe = Array.isArray(filenameBeMatches)
            ? typeof filenameBeMatches[1] === 'string'
                ? filenameBeMatches[1].replace(/\"/g, '').trim()
                : null
            : null;
        const filenameFallback = 'file';
        const filename = customFilename || filenameBe || filenameFallback;
        if (typeof window.navigator.msSaveBlob === 'function') {
            window.navigator.msSaveBlob(blob, filename);
        }
        else {
            const downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.setAttribute('download', filename);
            document.body.appendChild(downloadLink);
            downloadLink.click();
        }
    }
    rawFileDownload(blob, filename) {
        if (typeof window.navigator.msSaveBlob === 'function') {
            window.navigator.msSaveBlob(blob, filename);
        }
        else {
            const downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(blob);
            downloadLink.setAttribute('download', filename);
            document.body.appendChild(downloadLink);
            downloadLink.click();
        }
    }
    showTimeFromEpoch(value, format = this.DEFAULT_DATE_FORMAT) {
        if (!value) {
            return '';
        }
        const newValue = moment$2(value);
        return newValue.format(format);
    }
    onDeviceDownload() {
        const licenseKey = this.getLsUser()['licenseKey'];
        window.open(this.getAbsoluteRoute('deviceManagement.download', [this.getPlatformOs(), licenseKey]), '_blank');
    }
    isValidPin(pin) {
        return typeof pin === 'string' && pin.length === 4;
    }
    /**
     * @param originalName Name of object that's being duplicated
     * @param items All available items (to look for existing clone numbers)
     * @param getName Name extractor function, extracts name from the object
     */
    getCloneName(originalName, items, getName) {
        const pattern = /^(.*) - Clone (\d*)$/;
        const matches = originalName.match(pattern);
        const baseName = (Array.isArray(matches) && matches.length > 1 && matches[1]) || originalName;
        const clonedNumbers = items
            .filter((item) => typeof getName(item) === 'string' && getName(item).startsWith(baseName))
            .map((item) => {
            const name = getName(item);
            if (typeof name === 'string' && pattern.test(name)) {
                const matches2 = name.match(pattern);
                if (Array.isArray(matches2) && matches2.length > 2) {
                    return parseInt(matches2[2], 10);
                }
            }
            return 0;
        });
        if (!clonedNumbers.length) {
            return originalName;
        }
        const newClonedNumber = Math.max(...clonedNumbers) + 1;
        return `${baseName} - Clone ${newClonedNumber}`;
    }
    hasDuplicates(array) {
        return new Set(array).size !== array.length;
    }
    intersectArr(arr1, arr2) {
        if (!(Array.isArray(arr1) && Array.isArray(arr2))) {
            return [];
        }
        return arr1.filter((item) => arr2.includes(item));
    }
    /**
     * Checks if version is smaller than minVersion
     *
     * @param version 1.2.3.4
     * @param minVersion 2.0.0
     */
    satisfiesVersion(version, minVersion) {
        if (typeof version === 'string') {
            const versionArr = version.split('.');
            if (versionArr.length === 4) {
                versionArr.splice(3, 1);
                const standardSemver = versionArr.join('.');
                if (semver.satisfies(standardSemver, minVersion)) {
                    return true;
                }
            }
        }
        return false;
    }
    filterValuesToHuman(filters, selectedFilterIds) {
        if (!Array.isArray(filters)) {
            return selectedFilterIds;
        }
        const valueMap = {};
        for (const category of filters) {
            for (const child of category.children) {
                if (typeof child.id === 'string' && typeof child.name === 'string') {
                    valueMap[`${category.id}::${child.id}`] = child.name;
                }
            }
        }
        return selectedFilterIds.map((filterId) => valueMap[`${filterId.key}::${filterId.value}`] || filterId.value);
    }
    preventChars(event, chars) {
        return !chars.includes(event.key);
    }
    allowChars(event, chars) {
        if (chars.includes('numbers')) {
            chars = chars.filter((item) => item !== 'numbers');
            chars = [...chars, '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        }
        return chars.includes(event.key);
    }
    severitySort(event, extractVal, a, b) {
        const order = ['critical', 'important', 'moderate', 'low', 'unknown'];
        const aVal = extractVal(a);
        const bVal = extractVal(b);
        if (!(order.includes(aVal) && order.includes(bVal))) {
            return 0;
        }
        return event.direction === 'desc' ? order.indexOf(aVal) - order.indexOf(bVal) : order.indexOf(bVal) - order.indexOf(aVal);
    }
    alphaSort(event, extractVal, a, b) {
        const aVal = extractVal(a);
        const bVal = extractVal(b);
        if (aVal > bVal) {
            return event.direction === 'asc' ? -1 : 1;
        }
        if (bVal > aVal) {
            return event.direction === 'asc' ? 1 : -1;
        }
        return 0;
    }
    numSort(event, extractVal, a, b) {
        const aVal = extractVal(a);
        const bVal = extractVal(b);
        return event.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    get currentYear() {
        return new Date().getFullYear();
    }
    opEscapeHtml(str) {
        const escapeChars = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            "'": '&#39;',
            '"': '&quot;',
        };
        const htmlEscapeReg = new RegExp(`[${Object.keys(escapeChars)}]`, 'g');
        const escapeString = str.replace(htmlEscapeReg, (tag) => escapeChars[tag] || tag);
        return escapeString;
    }
    generateHTMLForDeviceAssignGroup(log) {
        const deviceName = log.device_name !== undefined ? this.opEscapeHtml(log.device_name) : '';
        const adminName = log.admin_name !== undefined ? this.opEscapeHtml(log.admin_name) : '';
        const groupName = log.group_name !== undefined ? this.opEscapeHtml(log.group_name) : '';
        const groupOldName = log.old_group_name !== undefined ? this.opEscapeHtml(log.old_group_name) : '';
        const adminEmail = log.admin_email !== undefined ? this.opEscapeHtml(log.admin_email) : '';
        const dataBinding = {
            '@deviceName': deviceName,
            '@adminName': adminName,
            '@groupName': groupName,
            '@groupOldName': groupOldName,
            '@adminEmail': adminEmail,
        };
        let device_assign_log = `Device <b>@deviceName</b> was moved from the group <b>@groupOldName</b>
    to <b>@groupName</b> by <b>@adminName</b> (@adminEmail)`;
        device_assign_log = device_assign_log.replace(/@deviceName|@adminName|@groupName|@groupOldName|@adminEmail/gi, (matched) => dataBinding[matched]);
        return device_assign_log;
    }
    getMALogo() {
        return this.getEnvironment().maLogo;
    }
    /**
     * Log to the console if not production
     * 'log' is default level, unless level is provided
     * **/
    log(value, level = 'log') {
        const css = 'padding:5px;color:#dadada;background-color:#282828;';
        const errorCss = 'padding:5px;color:#ffffff;background-color:#8f030f;';
        if (!this.getEnvironment().production) {
            try {
                // eslint-disable-next-line no-console
                console[level](`%c${value}`, level === 'error' ? errorCss : css);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error(`%c${err.message}`, errorCss);
            }
        }
    }
    isApiPinPost(link) {
        const listExtend = this.apiPinPost.map((item) => `${this.apiLink}${item}maRegexCheck`);
        return listExtend.includes(link);
    }
    isApiAddPin(link, method) {
        let listExtend;
        if (method === 'get') {
            listExtend = this.apiPinGet.map((item) => `${this.apiLink}${item}`);
        }
        else if (method === 'put') {
            listExtend = this.apiPinPut.map((item) => `${this.apiLink}${item}`);
        }
        else if (method === 'delete') {
            listExtend = this.apiPinDelete.map((item) => `${this.apiLink}${item}`);
        }
        const listFilter = listExtend.filter((item) => link.includes(item));
        return listFilter.length > 0;
    }
    async dataUriToBlob(data) {
        return await (await fetch(data)).blob();
    }
    static { this.ɵfac = function UtilsService_Factory(t) { return new (t || UtilsService)(i0.ɵɵinject(LOCALE_ID), i0.ɵɵinject('environment'), i0.ɵɵinject('constants')); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: UtilsService, factory: UtilsService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UtilsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [LOCALE_ID]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: ['environment']
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: ['constants']
            }] }]; }, null); })();

var CustomValidatorTypes;
(function (CustomValidatorTypes) {
    // eslint-disable-next-line
    let FirstCharacterType;
    (function (FirstCharacterType) {
        FirstCharacterType["not"] = "not";
        FirstCharacterType["is"] = "is";
    })(FirstCharacterType = CustomValidatorTypes.FirstCharacterType || (CustomValidatorTypes.FirstCharacterType = {}));
})(CustomValidatorTypes || (CustomValidatorTypes = {}));
class CustomValidators {
    constructor() { }
    /**
     *
     * fail input field is whitespace only
     * does not replace required
     *
     * @param control input value to check for all whitespace
     */
    noWhitespaceValidator(control) {
        // if value is empty, it does not have whitespace so return
        if (!control.value || !control.value.length) {
            return null;
        }
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }
    /**
     *
     * from a list of strings deterrmine if the current value exist and if it does
     * fail validation
     *
     * @param list list of strings to compare against
     */
    duplicateFromListValidator(list) {
        return (control) => {
            // if value is empty, it is not a duplicate so return
            if (!control.value || !control.value.length) {
                return null;
            }
            return !list.includes(control.value) ? null : { duplicate: true };
        };
    }
    isValidStartingCharacter(compareCharacter, type) {
        return (control) => {
            const firstCharacter = control.value.charAt(0);
            if ((type === CustomValidatorTypes.FirstCharacterType.is && compareCharacter === firstCharacter) ||
                (type === CustomValidatorTypes.FirstCharacterType.not && firstCharacter !== compareCharacter)) {
                return null;
            }
            return { invalidStartCharacter: { value: control.value } };
        };
    }
    isValidPin(control) {
        // if value is empty, it does not have whitespace so return
        if (!control.value || !control.value.length) {
            return null;
        }
        const isValid = typeof control.value === 'string' && control.value.length === 4;
        return isValid ? null : { pin: true };
    }
    static { this.ɵfac = function CustomValidators_Factory(t) { return new (t || CustomValidators)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CustomValidators, factory: CustomValidators.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CustomValidators, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return []; }, null); })();

class RbacService {
    constructor(utilsService) {
        this.utilsService = utilsService;
        this.roles = {};
        this.userRoleMap = {
            overview: 'dashboard_overview',
            'secure-access': 'dashboard_secure',
            vulnerability: 'dashboard_vul',
            compliance: 'dashboard_com',
            'threat-detection': 'dashboard_thread',
            apps: 'secure_pro',
            rules: 'secure_rules',
            activities: 'secure_acts',
            'access-methods': 'secure_method',
            vulnerabilities: 'vulnerabilities',
            applications: 'inventory_apps',
            'device-groups': 'inventory_group',
            policies: 'policy',
            users: 'user_users',
            sso: 'user_sso',
            'idp-groups': 'user_idp',
            roles: 'user_role',
            global: 'settings_global',
            integrations: 'settings_integ',
            'reports-notifications': 'settings_reports',
            rebranding: 'settings_rebranding',
            'notification-group': 'settings_notig',
            logs: 'logs',
            o: 'oauth_portal'
        };
        this.roles = this.utilsService.getEnvironment()['roles'];
    }
    can(role, operation) {
        if (!this.roles[role]) {
            return false;
        }
        if (!this.utilsService.isValidRole(role)) {
            return false;
        }
        const roleObject = this.roles[role];
        if (roleObject.can.indexOf(operation) !== -1) {
            return true;
        }
        if (!roleObject.inherits || roleObject.inherits.length < 1) {
            return false;
        }
        return roleObject.inherits.some(childRole => this.can(childRole, operation));
    }
    amAtLeast(role) {
        const activeUserRole = this.utilsService.getRole();
        if (!this.utilsService.isValidRole(activeUserRole)) {
            return false;
        }
        if (activeUserRole === role) {
            return true;
        }
        const roleObject = this.roles[activeUserRole];
        if (!roleObject.inherits || roleObject.inherits.length < 1) {
            return false;
        }
        return roleObject.inherits.some(childRole => role === childRole);
    }
    canI(operation, currentPage, isSpecial) {
        const activeUser = this.utilsService.getLsUser();
        if (!activeUser) {
            return false;
        }
        // just check for switch user
        if (activeUser.isMonitoring) {
            if (currentPage && isSpecial) {
                return currentPage === 'write';
            }
            else if (currentPage && !isSpecial) {
                const role = this.userRoleMap[currentPage];
                return activeUser.permissiongPage[role] === operation;
            }
            else {
                return this.can(activeUser['role'], operation);
            }
        }
        else {
            return this.can(activeUser['role'], operation);
        }
    }
    canEditHostName() {
        const activeUser = this.utilsService.getLsUser();
        if (!activeUser) {
            return false;
        }
        return !activeUser.privacy.hostname && !activeUser.hidden && this.canI('write');
    }
    accessedPermission(currentPage, withNoAdvance) {
        const activeUser = this.utilsService.getLsUser();
        if (!activeUser) {
            return false;
        }
        // just check for switch user
        if (activeUser.isMonitoring) {
            // Check default hide role and no any advance role for device, policy, group page
            if (withNoAdvance) {
                return (currentPage === 'false' || currentPage === 'hide') ? false : true;
            }
            else {
                const permissionUser = this.utilsService.getLsUser().permissiongPage;
                const role = this.userRoleMap[currentPage];
                return permissionUser[role] === 'hide' ? false : true;
            }
        }
        else {
            return true;
        }
    }
    static { this.ɵfac = function RbacService_Factory(t) { return new (t || RbacService)(i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: RbacService, factory: RbacService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RbacService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }]; }, null); })();

class SelectionService {
    constructor() {
        this.selection = null;
        this.key = 'id';
        this.selection = new SelectionModel(true, []);
    }
    isAllSelected(collection) {
        return collection
            .every(item => this.selection.selected
            .map(element => element[this.key])
            .indexOf(item[this.key]) > -1);
    }
    masterToggle(collection) {
        const toSelect = collection.filter(item => !this.isSelected(item));
        if (this.isAllSelected(collection)) {
            collection.forEach(device => this.toggle(device));
        }
        else {
            this.selection.select(...toSelect);
        }
    }
    isSelected(element) {
        return this.selection.selected.findIndex(item => item[this.key] === element[this.key]) > -1;
    }
    setKey(key) {
        this.key = key;
    }
    toggle(element) {
        if (this.isSelected(element)) {
            const tempSelected = this.selection.selected
                .filter(item => item[this.key] !== element[this.key]);
            this.selection.clear();
            this.selection.select(...tempSelected);
        }
        else {
            this.selection.select(element);
        }
    }
    static { this.ɵfac = function SelectionService_Factory(t) { return new (t || SelectionService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SelectionService, factory: SelectionService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SelectionService, [{
        type: Injectable
    }], function () { return []; }, null); })();

const moment$1 = moment_;
class UrlParamsService {
    constructor(router, utilsService, activatedRoute) {
        this.router = router;
        this.utilsService = utilsService;
        this.activatedRoute = activatedRoute;
        this.baseFieldMap = {
            cveId: 'cve_id',
            opswatScore: 'opswat_score',
            cvss2Score: 'cvss2_score',
            cvss3Score: 'cvss3_score',
            nrDevices: 'total_devices',
            appName: 'name',
            name: 'nickname',
            deviceUsername: 'user_name',
            lastReboot: 'last_reboot',
            lastSeen: 'last_seen',
            groupName: 'group_name',
            assignedPolicy: 'policy_name',
            devices: 'number_of_devices',
            lastUpdated: 'last_update',
            packageName: 'name',
            groupsAssigned: 'no_of_groups',
            numOfGroups: 'groups_assigned',
            last_modified: 'last_update', // sort on policy list
        };
        this.reservedKeywords = {
            search: ['search'],
            sort: ['sort'],
            page: ['page'],
            limit: ['limit']
        };
        this.params = {
            original: {
                page: null,
                age: null,
                limit: null,
                filters: null,
                search: null,
                sort: {
                    active: null,
                    direction: null
                }
            },
            current: {
                page: null,
                age: null,
                limit: null,
                filters: null,
                search: null,
                sort: {
                    active: null,
                    direction: null
                }
            },
            default: {
                page: 0,
                age: null,
                limit: 25,
                filters: {},
                search: '',
                sort: {
                    active: 'severity',
                    direction: 'desc'
                }
            }
        };
    }
    /**
     * Adds or removes a filter from this.params.current.filters object
     * event: { activated: string, direction: string }
     */
    toggleFilter(event, key, value) {
        if (key === 'age') {
            this.params.current.filters[key] = value;
            return;
        }
        let newValue = value;
        if (!this.params.current.filters.hasOwnProperty(key)) {
            this.params.current.filters[key] = value;
            return;
        }
        let parts = [];
        if (key !== 'age') {
            parts = this.params.current.filters[key].split(',');
        }
        const index = parts.indexOf(value);
        if (event.checked) {
            if (typeof this.params.current.filters[key] === 'string') {
                if (index === -1) {
                    parts.push(value);
                }
                newValue = parts.sort().join(',');
            }
        }
        else {
            if (index > -1) {
                parts.splice(index, 1);
            }
            newValue = parts.sort().join(',');
        }
        if (newValue.length) {
            this.params.current.filters[key] = newValue;
            return;
        }
        delete this.params.current.filters[key];
    }
    /**
     * Checks if value is present in this.params.current.filters[key] value
     */
    containsFilter(key, value) {
        if (!this.params.current.filters.hasOwnProperty(key)) {
            return false;
        }
        let parts = [];
        if (key !== 'age') {
            parts = this.params.current.filters[key].split(',');
        }
        if (!parts.length) {
            return false;
        }
        return parts.indexOf(value) > -1;
    }
    setDefaults(defaults) {
        this.params.default = defaults;
        this.params.current = JSON.parse(JSON.stringify(defaults));
    }
    getFilters(type = 'current', excludedKeys) {
        const filters = [];
        if (!Array.isArray(excludedKeys)) {
            excludedKeys = ['age', 'tab'];
        }
        else {
            if (!excludedKeys.includes('age')) {
                excludedKeys.push('age');
            }
            if (!excludedKeys.includes('tab')) {
                excludedKeys.push('tab');
            }
        }
        if (typeof this.params[type] === 'object' &&
            typeof this.params[type].filters === 'object') {
            Object
                .keys(this.params[type].filters)
                .forEach(key => {
                if (!excludedKeys.includes(key)) {
                    if (typeof this.params[type].filters[key] === 'string') {
                        filters.push(...this.params[type].filters[key]
                            .split(',')
                            .map(value => ({ key, value })));
                    }
                }
            });
        }
        return filters;
    }
    countFilters(type = 'current', excludedKeys) {
        return this.getFilters(type, excludedKeys).length;
    }
    /**
     * Parses router params into this.params.current object
     */
    parseRouteParams(params) {
        this.params.current = JSON.parse(JSON.stringify(this.params.default));
        Object.keys(params).forEach(key => {
            let entity = 'filters';
            Object.keys(this.reservedKeywords).forEach(reservedKeywordKey => {
                if (this.reservedKeywords[reservedKeywordKey].indexOf(key) > -1) {
                    entity = reservedKeywordKey;
                }
            });
            let parts = [];
            if (typeof params[key] === 'string') {
                parts = params[key].split(',');
            }
            if (Array.isArray(params[key])) {
                parts = params[key];
            }
            if (entity === 'filters') {
                if (['age'].indexOf(key) > -1) {
                    this.params.current[entity][key] = parseInt(params[key], 10);
                }
                else if (['from', 'to'].indexOf(key) > -1) {
                    if (moment$1(params[key]).isValid()) {
                        this.params.current[entity][key] = params[key];
                    }
                }
                else {
                    this.params.current[entity][key] = parts.sort().join(',');
                }
            }
            else if (entity === 'sort') {
                if (parts.length === 2) {
                    this.params.current[entity].active = parts[0];
                    this.params.current[entity].direction = parts[1];
                }
            }
            else if (['page', 'limit'].indexOf(entity) > -1) {
                if (parts.length === 1 && !Number.isNaN(parseInt(parts[0], 10))) {
                    this.params.current[entity] = parseInt(parts[0], 10);
                    if (entity === 'page') {
                        this.params.current.page -= 1;
                    }
                    if (this.params.current.page < 0) {
                        this.params.current.page = 0;
                    }
                }
            }
            else if (entity === 'search') {
                this.params.current[entity] = parts.join(',');
            }
            else {
                this.params.current[entity] = parts.sort().join(',');
            }
        });
        Object.keys(this.params.original).forEach(key => {
            delete this.params.original[key];
        });
        const currentCopy = JSON.parse(JSON.stringify(this.params.current));
        Object.assign(this.params.original, currentCopy);
    }
    hasEntityChanged(entity) {
        return JSON.stringify(this.params.current[entity]) !== JSON.stringify(this.params.original[entity]);
    }
    serializeSort() {
        return `${this.params.current.sort.active},${this.params.current.sort.direction}`;
    }
    onSort(sort) {
        Object.assign(this.params.current.sort, sort);
        this.onQueryParamsNavigate();
    }
    onUpdateFilters() {
        this.params.current.page = 0;
        this.onQueryParamsNavigate();
    }
    onChangePage(page) {
        this.params.current.page = page.pageIndex;
        this.params.current.limit = page.pageSize;
        this.onQueryParamsNavigate();
    }
    itemOnPage(total) {
        const startItem = this.params.current.limit * this.params.current.page + 1;
        // eslint-disable-next-line max-len
        const endItem = this.params.current.limit * (this.params.current.page + 1) > total ? total : this.params.current.limit * (this.params.current.page + 1);
        return `${startItem.toString()} - ${endItem}`;
    }
    itemOnChildPage(total, childLimit, childPage) {
        const startItem = childLimit * childPage + 1;
        // eslint-disable-next-line max-len
        const endItem = childLimit * (childPage + 1) > total ? total : childLimit * (childPage + 1);
        return `${startItem.toString()} - ${endItem}`;
    }
    getQueryParams() {
        /**
         * Reset page number on search
         * (so search doesn't go to let's say page 5 if
         * the search is performed on that page)
         */
        const didSearch = (!this.params.original.search &&
            this.params.current.search);
        const didFilter = (JSON.stringify(this.params.original.filters) !==
            JSON.stringify(this.params.current.filters));
        if ((didSearch || didFilter) &&
            !!this.params.current.page) {
            this.params.current.page = 0;
        }
        const serializedSort = this.serializeSort();
        const queryParams = {
            ...this.params.current.filters
        };
        if (this.params.current.page !== this.params.default.page) {
            queryParams.page = this.params.current.page + 1;
        }
        if (this.params.current.limit !== this.params.default.limit) {
            queryParams.limit = this.params.current.limit;
        }
        if ((this.params.current.sort.active !== this.params.default.sort.active ||
            this.params.current.sort.direction !== this.params.default.sort.direction) &&
            serializedSort.length > 0) {
            queryParams['sort'] = serializedSort;
        }
        if (this.params.current.search.length > 0) {
            queryParams['search'] = this.params.current.search.trim();
        }
        return queryParams;
    }
    onQueryParamsNavigate() {
        const params = {
            relativeTo: this.activatedRoute,
            queryParams: this.getQueryParams()
        };
        this.router.navigate([], params);
    }
    getApiFormattedParams(isOriginal, fieldMap = this.baseFieldMap) {
        let paramsData = this.params.current;
        if (isOriginal) {
            paramsData = this.params.original;
        }
        if (paramsData.limit > 100) {
            paramsData.limit = 100;
        }
        else if (paramsData.limit <= 0) {
            paramsData.limit = 25;
        }
        const { filters, search, sort, page, limit, age } = paramsData;
        const apiFormattedParams = {
            page: page + 1,
            limit,
            sort: {
                order: sort.direction,
                field: fieldMap[sort.active] || sort.active
            }
        };
        if (!this.utilsService.isObjectEmpty(filters)) {
            for (const key in filters) {
                if (['age'].includes(key)) {
                    apiFormattedParams[key] = filters[key];
                    if (apiFormattedParams[key] === '-1' || apiFormattedParams[key] === '1000') {
                        apiFormattedParams[key] = filters[key];
                    }
                }
                else if (['from', 'to'].includes(key)) {
                    const mapKey = {
                        from: 'start_date',
                        to: 'end_date'
                    };
                    apiFormattedParams[mapKey[key]] = moment$1(filters[key]).format('YYYY/MM/DD');
                }
                else {
                    if (!apiFormattedParams['filter']) {
                        apiFormattedParams['filter'] = {};
                    }
                    apiFormattedParams['filter'][key] = filters[key].split(',');
                }
            }
        }
        if (typeof search === 'string' && search.length > 0) {
            apiFormattedParams['search'] = search.trim();
        }
        return apiFormattedParams;
    }
    getParams() {
        return this.params;
    }
    onClearSearchField() {
        this.params.current.search = '';
        this.onQueryParamsNavigate();
    }
    onResetFilters() {
        this.params.current.filters = this.params.default.filters;
        this.onQueryParamsNavigate();
    }
    static { this.ɵfac = function UrlParamsService_Factory(t) { return new (t || UrlParamsService)(i0.ɵɵinject(i1.Router), i0.ɵɵinject(UtilsService), i0.ɵɵinject(i1.ActivatedRoute)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: UrlParamsService, factory: UrlParamsService.ɵfac }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UrlParamsService, [{
        type: Injectable
    }], function () { return [{ type: i1.Router }, { type: UtilsService }, { type: i1.ActivatedRoute }]; }, null); })();

class LoadingService {
    constructor() {
        this.emitter = new EventEmitter();
    }
    start(signature) {
        if (typeof signature === 'string') {
            const status = true;
            this.emitter.emit({ signature, status });
        }
    }
    complete(signature) {
        if (typeof signature === 'string') {
            const status = false;
            this.emitter.emit({ signature, status });
        }
    }
    static { this.ɵfac = function LoadingService_Factory(t) { return new (t || LoadingService)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: LoadingService, outputs: { emitter: "emitter" } }); }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: LoadingService, factory: LoadingService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoadingService, [{
        type: Directive
    }, {
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, { emitter: [{
            type: Output
        }] }); })();

/**
 * How many minutes before JWT token expiration
 * do we attempt to refresh the token?
 */
const SHOW_SESSION_EXTENSION_POPUP_BEFORE_EXP_MIN = 10;
/**
 * How many minutes of inactivity makes the
 * user being considered idle?
 */
const ACTIVITY_ASSESSMENT_PERIOD_MIN = 15;
class SessionService {
    constructor(utilsService) {
        this.utilsService = utilsService;
        this.sessionExpired = new EventEmitter();
        this.refreshToken = new EventEmitter();
        this.showSessionExtensionPopup = new EventEmitter();
    }
    isTokenInLocalStorage() {
        const token = localStorage.getItem('token');
        return typeof token === 'string' && !!token.length;
    }
    getEncryptedJwtToken() {
        const token = localStorage.getItem('token');
        return decodeURIComponent(token);
    }
    getJwtToken() {
        const token = this.getEncryptedJwtToken();
        return this.utilsService.decrypt(token);
    }
    getJwtDecodedToken() {
        const token = this.getJwtToken();
        return this.decodeJwt(token);
    }
    getJwtExpiration() {
        const token = this.getJwtDecodedToken();
        return (token && token.exp) || null;
    }
    /**
     * init function gets called in two cases:
     * - retrieving and saving new token to LS (saveToken function below)
     * - app initialization (initSessionExpiration function in app.module.ts)
     */
    init(source) {
        this.utilsService.log(`${new Date()}: Session init from ${source}`);
        /**
         * Case 0:
         * token not found, nothing to do at this
         * point, emit sessionExpired event
         */
        if (!this.isTokenInLocalStorage()) {
            this.utilsService.log(`${new Date()}: token-not-found, case 0`);
            this.sessionExpired.emit('token-not-found');
            return;
        }
        const timestamp = this.getJwtExpiration();
        /**
         * Case 1:
         * timestamp is not a number, nothing to do
         * at this point, emit sessionExpired event
         */
        if (typeof timestamp !== 'number') {
            this.utilsService.log(`${new Date()}: Session init, case 1`);
            this.sessionExpired.emit('jwt-timestamp-not-found');
            return;
        }
        const jwtExpiresInMinutes = this.getMillisUntilTimestamp(timestamp) / 60000;
        /**
         * Case 2:
         * JWT expires in more than 10 minutes
         */
        if (jwtExpiresInMinutes >= SHOW_SESSION_EXTENSION_POPUP_BEFORE_EXP_MIN) {
            this.utilsService.log(`${new Date()}: Session init, case 2`);
            this.scheduleSessionExtensionAttempt();
            this.scheduleSessionExpiration();
            return;
        }
        /**
         * Case 3:
         * JWT expires in less than 10 minutes
         */
        if (jwtExpiresInMinutes < SHOW_SESSION_EXTENSION_POPUP_BEFORE_EXP_MIN &&
            jwtExpiresInMinutes > 0) {
            this.utilsService.log(`${new Date()}: Session init, case 3`);
            this.attemptSessionExtension();
            this.scheduleSessionExpiration();
            return;
        }
        /**
         * Case 4:
         * JWT already expired
         */
        if (jwtExpiresInMinutes <= 0) {
            this.utilsService.log(`${new Date()}: Session init, case 4`);
            this.sessionExpired.emit('jwt-already-expired');
            return;
        }
    }
    /**
     * Emit session expiration event based
     * on JWT expiration time
     */
    scheduleSessionExpiration() {
        const timestamp = this.getJwtExpiration();
        const jwtExpiresInMillis = this.getMillisUntilTimestamp(timestamp);
        if (this.sessionExpirationTimeout) {
            clearTimeout(this.sessionExpirationTimeout);
        }
        const msg = `${new Date()}: Will log user out in ${Math.round(jwtExpiresInMillis / 6000) / 10} minutes unless session is extended`;
        this.utilsService.log(msg);
        this.sessionExpirationTimeout = setTimeout(() => {
            this.sessionExpired.emit('scheduled-logout');
        }, jwtExpiresInMillis);
    }
    /**
     * Attempt session extension based
     * on JWT expiration time
     */
    scheduleSessionExtensionAttempt() {
        const timestamp = this.getJwtExpiration();
        const jwtExpiresInMillis = this.getMillisUntilTimestamp(timestamp);
        const attemptSessionExtensionInMillis = jwtExpiresInMillis - (SHOW_SESSION_EXTENSION_POPUP_BEFORE_EXP_MIN * 60000);
        if (this.sessionExtensionPopupTimeout) {
            clearTimeout(this.sessionExtensionPopupTimeout);
        }
        const msg = `${new Date()}: Will attempt session extension in ${Math.round(attemptSessionExtensionInMillis / 6000) / 10} minutes`;
        this.utilsService.log(msg);
        this.sessionExtensionPopupTimeout = setTimeout(() => {
            this.attemptSessionExtension();
        }, attemptSessionExtensionInMillis);
    }
    /**
     * Attempt session extension based on
     * user activity.
     * If user is active => refresh token, else offer session
     * extension popup
     */
    attemptSessionExtension() {
        if (this.isUserActive()) {
            this.utilsService.log(`${new Date()}: User is active, attempting token refresh`);
            this.refreshToken.emit();
            return;
        }
        this.utilsService.log(`${new Date()}: User is not active, skipping refresh token attempt, offering session extension popup`, 'warn');
        this.showSessionExtensionPopup.emit();
    }
    saveToken(token, source) {
        this.utilsService.log(`${new Date()}: Putting JWT into local storage, source ${source}`);
        localStorage.setItem('token', token);
        this.init('save-token');
    }
    getMillisUntilTimestamp(timestamp) {
        if (typeof timestamp !== 'number') {
            return 0;
        }
        timestamp *= 1000;
        return (timestamp - Date.now());
    }
    isUserActive() {
        const now = Date.now();
        const lastActivity = localStorage.getItem('la');
        this.utilsService.log(`${new Date()}: Last activity at ${lastActivity}`);
        return (typeof lastActivity === 'string' &&
            !Number.isNaN(Number(lastActivity)) &&
            now - parseInt(lastActivity, 10) < (ACTIVITY_ASSESSMENT_PERIOD_MIN * 60000));
    }
    decodeJwt(token) {
        if (!(typeof token === 'string' && token.length)) {
            return null;
        }
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const mapFce = char => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2);
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map(mapFce)
            .join(''));
        return JSON.parse(jsonPayload);
    }
    static { this.ɵfac = function SessionService_Factory(t) { return new (t || SessionService)(i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SessionService, factory: SessionService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SessionService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }]; }, null); })();

class HttpService extends HttpClient {
    constructor(httpHandler, loadingService, sessionService, utilsService) {
        super(httpHandler);
        this.httpHandler = httpHandler;
        this.loadingService = loadingService;
        this.sessionService = sessionService;
        this.utilsService = utilsService;
        this.apiUrl = this.utilsService.getApiUrl();
    }
    get(url, options) {
        this.loadingService.start(`get${url}`);
        options = options || {};
        Object.assign(options, {
            observe: 'response'
        });
        if (this.utilsService.isApiAddPin(`${url}`, 'get') && !!this.utilsService.veriyPIN['pin']) {
            Object.assign(options, { headers: {
                    // eslint-disable-next-line
                    'pin': this.utilsService.veriyPIN['pin']
                } });
        }
        return super.get(url, options).pipe(tap((res) => {
            this.loadingService.complete(`get${url}`);
            this.catchAccountNotFound(res);
            this.resetPin(url, res);
        }, (err) => {
            this.loadingService.complete(`get${url}`);
        }), switchMap(this.mapToError.bind(this)), catchError(this.catchError.bind(this)));
    }
    post(url, body, options) {
        this.loadingService.start(`post${url}`);
        options = options || {};
        Object.assign(options, {
            observe: 'response'
        });
        if (this.utilsService.isApiPinPost(`${url}maRegexCheck`) && !!this.utilsService.veriyPIN['pin']) {
            Object.assign(options, { headers: {
                    // eslint-disable-next-line
                    'pin': this.utilsService.veriyPIN['pin']
                } });
        }
        return super.post(url, body, options).pipe(tap((res) => {
            this.loadingService.complete(`post${url}`);
            this.catchAccountNotFound(res);
            this.resetPin(url, res);
        }, (err) => {
            this.loadingService.complete(`post${url}`);
        }), switchMap(this.mapToError.bind(this)), catchError(this.catchError.bind(this)));
    }
    put(url, body, options) {
        this.loadingService.start(`put${url}`);
        options = options || {};
        Object.assign(options, {
            observe: 'response'
        });
        if (this.utilsService.isApiAddPin(`${url}`, 'put') && !!this.utilsService.veriyPIN['pin']) {
            Object.assign(options, { headers: {
                    // eslint-disable-next-line
                    'pin': this.utilsService.veriyPIN['pin']
                } });
        }
        return super.put(url, body, options).pipe(tap((res) => {
            this.loadingService.complete(`put${url}`);
            this.catchAccountNotFound(res);
            this.resetPin(url, res);
        }, (err) => {
            this.loadingService.complete(`put${url}`);
        }), switchMap(this.mapToError.bind(this)), catchError(this.catchError.bind(this)));
    }
    delete(url, options) {
        this.loadingService.start(`delete${url}`);
        options = options || {};
        Object.assign(options, {
            observe: 'response'
        });
        if (this.utilsService.isApiAddPin(`${url}`, 'delete') && !!this.utilsService.veriyPIN['pin']) {
            Object.assign(options, { headers: {
                    // eslint-disable-next-line
                    'pin': this.utilsService.veriyPIN['pin']
                } });
        }
        return super.delete(url, options).pipe(tap((res) => {
            this.loadingService.complete(`delete${url}`);
            this.catchAccountNotFound(res);
            this.resetPin(url, res);
        }, (err) => {
            this.loadingService.complete(`delete${url}`);
        }), switchMap(this.mapToError.bind(this)), catchError(this.catchError.bind(this)));
    }
    resetPin(url, response) {
        if (response && !url.includes('/pin/confirm')) {
            this.utilsService.veriyPIN = '';
        }
    }
    // Helper functions
    mapToError(response) {
        if (response.body && response.body.status === false) {
            const errorResponse = new HttpErrorResponse({
                status: response.status,
                statusText: response.statusText
            });
            response = errorResponse;
        }
        if (response instanceof HttpErrorResponse) {
            return throwError(response);
        }
        return of(response);
    }
    catchError(errorResponse) {
        if (errorResponse.status === 401) {
            this.sessionService.sessionExpired.emit('403');
        }
        return throwError(errorResponse);
    }
    catchAccountNotFound(res) {
        if (res.statusText === 'account_not_found') {
            this.utilsService.accountNotFound.emit('account_not_found');
        }
    }
    static { this.ɵfac = function HttpService_Factory(t) { return new (t || HttpService)(i0.ɵɵinject(i1$1.HttpHandler), i0.ɵɵinject(LoadingService), i0.ɵɵinject(SessionService), i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HttpService, factory: HttpService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: i1$1.HttpHandler }, { type: LoadingService }, { type: SessionService }, { type: UtilsService }]; }, null); })();

class InitializeService {
    constructor(utilsService) {
        this.utilsService = utilsService;
    }
    setTheme() {
        const preferredTheme = this.utilsService.getPreferredTheme();
        const defaultTheme = this.utilsService.getEnvironment()['theme'];
        const theme = preferredTheme || defaultTheme;
        const setThemePromise = this.utilsService.setTheme(theme);
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(resolve, 5000, '');
        });
        return Promise.race([setThemePromise, timeoutPromise]);
    }
    static { this.ɵfac = function InitializeService_Factory(t) { return new (t || InitializeService)(i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: InitializeService, factory: InitializeService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(InitializeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }]; }, null); })();

class ErrorResponseInterceptor {
    constructor(utilsService) {
        this.utilsService = utilsService;
    }
    intercept(request, next) {
        return next.handle(request).pipe(map(event => {
            if (event instanceof HttpResponse) {
                const body = event.body;
                if (body && ['true', 'false'].includes(body.status)) {
                    body.status = body.status === 'true';
                }
                if (body && body.status === false) {
                    const statusText = body.msg || body.reason || 'Something went wrong';
                    const status = this.utilsService.mapErrorMessageToStatus(statusText);
                    return new HttpResponse({ body, status, statusText });
                }
            }
            return event;
        }));
    }
    static { this.ɵfac = function ErrorResponseInterceptor_Factory(t) { return new (t || ErrorResponseInterceptor)(i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: ErrorResponseInterceptor, factory: ErrorResponseInterceptor.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ErrorResponseInterceptor, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }]; }, null); })();

class AuthenticationGuard {
    constructor(utilsService, sessionService) {
        this.utilsService = utilsService;
        this.sessionService = sessionService;
    }
    canActivate(next, state) {
        if (this.utilsService.getLsUser()) {
            return true;
        }
        this.sessionService.sessionExpired.emit('no-ls-user');
        return false;
    }
    static { this.ɵfac = function AuthenticationGuard_Factory(t) { return new (t || AuthenticationGuard)(i0.ɵɵinject(UtilsService), i0.ɵɵinject(SessionService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AuthenticationGuard, factory: AuthenticationGuard.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationGuard, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }, { type: SessionService }]; }, null); })();

class RouteAccessGuard {
    constructor(router, rbacService) {
        this.router = router;
        this.rbacService = rbacService;
    }
    canActivate(next, state) {
        const data = next.data ? next.data : null;
        if (data && typeof data === 'object' && data.accessibleBy) {
            if (this.rbacService.amAtLeast(data.accessibleBy)) {
                return true;
            }
        }
        return false;
    }
    static { this.ɵfac = function RouteAccessGuard_Factory(t) { return new (t || RouteAccessGuard)(i0.ɵɵinject(i1.Router), i0.ɵɵinject(RbacService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: RouteAccessGuard, factory: RouteAccessGuard.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RouteAccessGuard, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: i1.Router }, { type: RbacService }]; }, null); })();

const moment = moment_;
class SimpleUrlParamsService {
    constructor(params) {
        this.params = {};
        this.params = params;
    }
    toggleFilter(event, key, value) {
        const path = key.split('.');
        let arr = this.params;
        path.forEach(segment => {
            arr = arr[segment];
        });
        if (Array.isArray(arr)) {
            if (event.checked) {
                arr.push(value);
            }
            else {
                const index = arr.findIndex(item => item === value);
                arr.splice(index, 1);
            }
        }
        else {
            arr = [value];
        }
    }
    getParams() {
        return this.params;
    }
    onChangeTimeframe(startdate, enddate, customDateNumber, dateFormat) {
        const age = this.params['age'];
        this.params['start_date'] = age !== customDateNumber ? null : moment(startdate).format(dateFormat);
        this.params['end_date'] = age !== customDateNumber ? null : moment(enddate).format(dateFormat);
    }
    getFilters() {
        const filters = [];
        if (typeof this.params === 'object' &&
            typeof this.params['filter'] === 'object') {
            const cat = Object.keys(this.params['filter']);
            cat.forEach(item => {
                Object
                    .keys(this.params['filter'][item])
                    .forEach(key => {
                    if (typeof this.params['filter'][item][key] === 'string') {
                        filters.push(...this.params['filter'][item][key]
                            .split(',')
                            .map(value => ({ key: item, value })));
                    }
                });
            });
        }
        return filters;
    }
    getFilterIds(existCat) {
        if (existCat) {
            return this.params['filter'][existCat];
        }
        else {
            const cat = Object.keys(this.params['filter']);
            return this.params['filter'][cat];
        }
    }
}

class FeatureService {
    constructor(utilsService) {
        this.utilsService = utilsService;
    }
    hasFeature(featureName) {
        const flags = this.utilsService.getFeatureFlags();
        if (typeof featureName !== 'string' ||
            !featureName ||
            typeof flags !== 'object') {
            return false;
        }
        return !!flags[featureName];
    }
    static { this.ɵfac = function FeatureService_Factory(t) { return new (t || FeatureService)(i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: FeatureService, factory: FeatureService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FeatureService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }]; }, null); })();

class ServicesModule {
    static forRoot(environment, constants) {
        return {
            ngModule: ServicesModule,
            providers: [{
                    provide: 'environment',
                    useValue: environment
                }, {
                    provide: 'constants',
                    useValue: constants
                }]
        };
    }
    static { this.ɵfac = function ServicesModule_Factory(t) { return new (t || ServicesModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: ServicesModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({}); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ServicesModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [],
                exports: []
            }]
    }], null, null); })();

class OauthAuthenticationGuard {
    constructor(utilsService, router) {
        this.utilsService = utilsService;
        this.router = router;
    }
    canActivate(route, state) {
        if (!this.utilsService.getLsUser().isMonitoring) {
            return true;
        }
        this.router.navigate(['403']);
        return false;
    }
    static { this.ɵfac = function OauthAuthenticationGuard_Factory(t) { return new (t || OauthAuthenticationGuard)(i0.ɵɵinject(UtilsService), i0.ɵɵinject(i1.Router)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: OauthAuthenticationGuard, factory: OauthAuthenticationGuard.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OauthAuthenticationGuard, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }, { type: i1.Router }]; }, null); })();

class QrcodeService {
    constructor(utilService) {
        this.utilService = utilService;
        this.qrObject = null;
        this.data = null;
        this.URI = null;
        this.scale = null;
        this.margin = null;
        this.width = null;
    }
    generateQrCode(data) {
        this.qrObject = QRCode.create(data, {});
        this.data = data;
        return this;
    }
    setScale(scale) {
        this.scale = scale;
        return this;
    }
    setMargin(margin) {
        this.margin = margin;
        return this;
    }
    setWidth(width) {
        this.width = width;
        return this;
    }
    toDataURL() {
        QRCode.toDataURL(this.qrObject.segments, {
            type: 'image/png',
            color: {
                dark: '#183B7F',
                light: '#FFFFFF'
            },
            scale: this.scale,
            margin: this.margin,
            width: this.width
        }, (err, url) => {
            this.URI = url;
        });
        return this.URI;
    }
    toBlob() {
        const URI = this.toDataURL();
        return this.utilService.dataUriToBlob(URI);
    }
    static { this.ɵfac = function QrcodeService_Factory(t) { return new (t || QrcodeService)(i0.ɵɵinject(UtilsService)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: QrcodeService, factory: QrcodeService.ɵfac, providedIn: 'root' }); }
}
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(QrcodeService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: UtilsService }]; }, null); })();

/*
 * Public API Surface of opswat-services
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthenticationGuard, CustomValidatorTypes, CustomValidators, ErrorResponseInterceptor, FeatureService, HttpService, InitializeService, LoadingService, OauthAuthenticationGuard, QrcodeService, RbacService, RouteAccessGuard, SelectionService, ServicesModule, SessionService, SimpleUrlParamsService, UrlParamsService, UtilsService };
